// Outer
import * as Sentry from '@sentry/nextjs';
import React, { useState, useEffect, } from 'react';
import {
  Alert,
  Grid,
  Button,
  Box,
  Typography,
  Card,
  CardActionArea,
  TextField,
  Link,
} from '@mui/material';
import { useRouter } from 'next/router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp, library } from '@fortawesome/fontawesome-svg-core';
import { fas, faTrash } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from 'react-query';

// Inner
import Heading from '@/common/components/Heading';
import HeaderBar from '@/common/layouts/HeaderBar';
import DefaultLayout from '@/common/layouts/DefaultLayout';
import Division from '@/common/components/Division';
import SimpleDrawer from '@/common/components/SimpleDrawer';
import Footer from '@/common/layouts/Footer';
import TabLayout from '@/common/layouts/TabLayout';
import { getServerSidePropsPrefetch } from '@/common/utils/ssr';
import { queries } from '@/common/api/operations';
import { useApiContext } from '@/common/contexts/ApiContext';
import { ssrMenuItems } from '@/common/hooks/useMenuItems';
import { appRoutes } from '@/common/config';
import { KNOWLEDGE_BASE_DRAWER_TABS } from './tabs';
import { useUserContext } from '@/common/contexts/UserContext';
import { ApiError } from '@/common/api/types';
import UpdateKnowledgeBaseForm from '@/common/pages/home/knowledgeBase/knowledgeBase/UpdateKnowledgeBaseForm';
import { useSanitizeHtml } from '@/common/hooks/useSanitizeHtml';

library.add(fas, far);

export default function KnowledgeBasePage() {
  const { push } = useRouter();
  const [isKnowledgebaseDrawerCreate, setKnowledgebaseDrawerCreate] = useState(false);
  const [isKnowledgebaseDrawerUpdate, setKnowledgebaseDrawerUpdate] = useState(false);
  const [textSearch, setTextSearch] = useState('');
  const [knowledgebaseId, setKnowledgebaseId] = useState(0);

  const { apiClient } = useApiContext();
  const { isAdmin }: any = useUserContext();
  const { enqueueSnackbar } = useSnackbar();

  const knowledgebaseQuery = useQuery(
    queries.knowledgebase.pagination,
    () => apiClient.knowledgebase.pagination(textSearch)
  );
  const allKnowledgebase = knowledgebaseQuery?.data || [];

  const knowledgebasecategoriesQuery = useQuery(
    queries.knowledgebasecategories.get,
    () => apiClient.knowledgebasecategories.get()
  );
  const allKnowledgebasecategories = knowledgebasecategoriesQuery?.data || [];

  useEffect(() => {
    knowledgebaseQuery.refetch();
  }, [textSearch])

  const handleDissmissKnowledgebaseDrawerCreate = () => {
    setKnowledgebaseDrawerCreate(false);
    push(appRoutes.static.knowledgeBase());
  };

  const handleTabChange = React.useCallback(
    (tabTitle?: string) => {
      push(appRoutes.static.knowledgeBase(tabTitle));
    },
    [push],
  );

  const handleDissmissKnowledgebaseDrawerUpdate = () => {
    setKnowledgebaseDrawerUpdate(false);
    setKnowledgebaseId(0);
  };

  const handleSinglePage = React.useCallback(
    (categoryId?: number) => {
      push(appRoutes.static.knowledgeBaseSingle(categoryId));
    },
    [push],
  );

  const handleTextChange = ({
    target: { value },
  }: React.ChangeEvent<{ value: string }>) => {
    setTextSearch(value);
  };

  function onSuccess() {
    enqueueSnackbar('Knowledge Base deleted', { variant: 'success' });
    knowledgebaseQuery.refetch();
  }
  function onError(error: ApiError | Error) {
    enqueueSnackbar(error.message, { variant: 'error' });
    Sentry.captureException(error);
  }

  const deleteKnowledgeBaseMutation = useMutation<
    any,
    ApiError | Error,
    any
  >(apiClient.knowledgebase.delete, {
    onSuccess,
    onError
  })

  const deleteKnowladgeBase = (id: number) => {
    deleteKnowledgeBaseMutation.mutate({ id });
  }

  return (
    <>
      <HeaderBar />
      <DefaultLayout>
        <SimpleDrawer
          anchor="right"
          size="large"
          isOpen={isKnowledgebaseDrawerCreate}
          strictClose={true}
          onClose={handleDissmissKnowledgebaseDrawerCreate}
        >
          <TabLayout
            tabs={KNOWLEDGE_BASE_DRAWER_TABS}
            onTabChange={handleTabChange}
          >
            {TabComponent => (
              <TabComponent
                onClose={handleDissmissKnowledgebaseDrawerCreate}
                refetchKnowledgebasecategory={knowledgebasecategoriesQuery.refetch}
                knowledgeBaseCategories={allKnowledgebasecategories}
              />
            )}
          </TabLayout>
        </SimpleDrawer>

        <SimpleDrawer
          anchor="right"
          size="large"
          isOpen={isKnowledgebaseDrawerUpdate}
          strictClose={true}
          onClose={handleDissmissKnowledgebaseDrawerUpdate}
        >
          <UpdateKnowledgeBaseForm
            refetch={knowledgebaseQuery.refetch}
            onClose={handleDissmissKnowledgebaseDrawerUpdate}
            knowledgebaseId={knowledgebaseId}
            knowledgeBases={allKnowledgebase}
            knowledgeBaseCategories={allKnowledgebasecategories}
          />
        </SimpleDrawer>

        <Grid container direction="column">
          <Box display="flex" justifyContent="space-between" marginBottom={3}>
            <Heading>Knowledge Base</Heading>
            {
              isAdmin && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setKnowledgebaseDrawerCreate(true)}
                >
                  New Knowledge Base
                </Button>
              )
            }
          </Box>

          <Box marginBottom={2} display="flex" alignItems="center">
            <Box width="50%">
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search Knowledge Base..."
                aria-label="search"
                type="text"
                name="text"
                value={textSearch}
                onChange={handleTextChange}
              />
            </Box>
            <Box width="50%" paddingLeft={2}>
              {
                textSearch !== "" && (
                  <Button
                    color="primary"
                    onClick={() => setTextSearch('')}
                  >
                    Clear Search
                  </Button>
                )
              }
            </Box>
          </Box>

          <Box marginBottom={2} width={"100%"}>
            {
              (!!allKnowledgebasecategories?.length && textSearch === '') && (
                <Grid
                  container direction="row"
                >
                  {
                    allKnowledgebasecategories.map((item: any, idx: number) => {
                      return (
                        <Grid
                          key={idx}
                          item xs={12} sm={4}
                          style={{ textAlign: 'center', padding: '0 2px 5px' }}
                        >
                          <Card>
                            <CardActionArea
                              style={{ padding: '50px 30px' }}
                              className='knowledge-base-list__holder'
                              onClick={() => handleSinglePage(item.id)}
                            >
                              <Box marginBottom={2} className='knowledge-base-list__icon'>
                                <span>
                                  <FontAwesomeIcon
                                    size="2x"
                                    color='#fff'
                                    icon={item.iconName as IconProp}
                                  />
                                </span>
                              </Box>
                              <Typography variant="h5" component="span">{item.name.toUpperCase()}</Typography>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      )
                    })
                  }
                </Grid>
              )
            }
            {
              (!!allKnowledgebase?.length && textSearch !== '') && allKnowledgebase?.map((item: any) => {
                return (
                  <Box
                    marginBottom={4}
                    key={item.id}
                    maxWidth={800}
                    style={{ position: "relative" }}
                  >
                    {
                      isAdmin && (
                        <Link
                          style={{
                            cursor: "pointer",
                            position: "absolute",
                            right: -18,
                            top: 5
                          }}
                          onClick={() => deleteKnowladgeBase(item.id)}
                        >
                          <FontAwesomeIcon
                            size="1x"
                            icon={faTrash}
                          />
                        </Link>
                      )
                    }

                    <Typography variant="h6" gutterBottom={true} >{item.question}</Typography>
                    {
                      isAdmin ? (
                        <Link
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setKnowledgebaseId(item.id);
                            setKnowledgebaseDrawerUpdate(true);
                          }}
                        >
                          {
                            item?.answer && (
                              <div
                                className='clear-indents'
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                dangerouslySetInnerHTML={{ __html: useSanitizeHtml(item.answer) }}
                              />
                            )
                          }
                        </Link>
                      ) : (
                        <>
                          {
                            item?.answer && (
                              <div
                                className='clear-indents'
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                dangerouslySetInnerHTML={{ __html: useSanitizeHtml(item.answer) }}
                              />
                            )
                          }
                        </>
                      )
                    }
                  </Box>
                )
              })
            }
            {
              (!allKnowledgebasecategories?.length || textSearch !== '' && !allKnowledgebase?.length) && (
                <Alert severity="info">
                  <Typography variant="body1">
                    {`No Knowledge Base Articles found.`}
                  </Typography>
                </Alert>
              )
            }
          </Box>

          <Division />
        </Grid>
      </DefaultLayout>
      <Footer />
    </>
  );
}

export const getServerSideProps = getServerSidePropsPrefetch([
  ssrMenuItems,
]);
